import React, { useState } from 'react';
import { claimableItemsService } from '../../services/claimableItemsService';
import type { ClaimableItem, ClaimCategory } from '../../services/claimableItemsService';
import { CAT_LABELS, CAT_OPTIONS, CAT_COLORS, CAT_BG, btnPrimary, btnSecondary, btnDanger } from './claimsConstants';

export interface ClaimableItemsStepProps {
  subProjectId: string;
  solutionId: string;
  items: ClaimableItem[];
  onItemsChange: (items: ClaimableItem[]) => void;
  onNext: () => void;
  t: (k: string, fb?: string) => string;
}

const ClaimableItemsStep: React.FC<ClaimableItemsStepProps> = ({ subProjectId, solutionId, items, onItemsChange, onNext, t }) => {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newCat, setNewCat] = useState<ClaimCategory>('product');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setSaving(true); setError(null);
    try {
      const item = await claimableItemsService.create(subProjectId, {
        label: newLabel.trim(), category: newCat,
        linked_solution_ids: solutionId ? [solutionId] : [],
      });
      onItemsChange([...items, item]);
      setNewLabel(''); setNewCat('product'); setAdding(false);
    } catch (e: unknown) {
      setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('claimsWorkspace.step1.createError', 'Failed to create item'));
    } finally { setSaving(false); }
  };

  const handleSaveEdit = async () => {
    if (!editId || !editLabel.trim()) return;
    setSaving(true); setError(null);
    try {
      const updated = await claimableItemsService.update(subProjectId, editId, { label: editLabel.trim() });
      onItemsChange(items.map(i => i.id === editId ? updated : i));
      setEditId(null);
    } catch (e: unknown) {
      setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('claimsWorkspace.step1.updateError', 'Failed to update item'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await claimableItemsService.delete(subProjectId, id);
      onItemsChange(items.filter(i => i.id !== id));
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: { linked_claims_count?: number } | string } } })?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.linked_claims_count) {
        if (window.confirm(t('claimsWorkspace.step1.forceDeleteConfirm', `This item has ${detail.linked_claims_count} linked claim(s). Delete anyway?`))) {
          await claimableItemsService.delete(subProjectId, id, true);
          onItemsChange(items.filter(i => i.id !== id));
        }
      } else {
        setError(typeof detail === 'string' ? detail : t('claimsWorkspace.step1.deleteError', 'Failed to delete item'));
      }
    }
  };

  return (
    <div style={{ padding: '12px 16px', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', flex: 1 }}>
          {t('claimsWorkspace.step1.title', 'Define Claimable Items')}
        </span>
        {!adding && (
          <button onClick={() => setAdding(true)} style={btnSecondary}>
            + {t('claimsWorkspace.step1.addItem', 'Add item')}
          </button>
        )}
        <button
          onClick={onNext}
          disabled={items.length === 0}
          style={{ ...btnPrimary, opacity: items.length === 0 ? 0.4 : 1, cursor: items.length === 0 ? 'default' : 'pointer' }}
        >
          {t('claimsWorkspace.step1.next', 'Start ranking →')}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 3, padding: '4px 8px', marginBottom: 8 }}>
          {error}
        </div>
      )}

      {items.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: '#94a3b8', padding: '12px 0' }}>
          {t('claimsWorkspace.step1.empty', 'No items yet. The agent will suggest items from your solution, or add one manually.')}
        </div>
      )}

      {items.length > 0 && (
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', marginBottom: 8 }}>
          <thead>
            <tr style={{ background: '#f8fafc', color: '#64748b' }}>
              <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{t('claimsWorkspace.step1.colLabel', 'Item')}</th>
              <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{t('claimsWorkspace.step1.colCategory', 'Category')}</th>
              <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{t('claimsWorkspace.step1.colRank', 'Ranking')}</th>
              <th style={{ padding: '4px 6px', borderBottom: '1px solid #e2e8f0' }} />
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '5px 6px' }}>
                  {editId === item.id ? (
                    <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                      style={{ fontSize: 11, padding: '2px 5px', border: '1px solid #94a3b8', borderRadius: 3, width: '100%' }} />
                  ) : (
                    <span style={{ color: '#1e293b', fontWeight: 500 }}>{item.label}</span>
                  )}
                </td>
                <td style={{ padding: '5px 6px' }}>
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: CAT_BG[item.category] ?? '#e0e7ff', color: CAT_COLORS[item.category] ?? '#3730a3', fontWeight: 700, textTransform: 'uppercase' }}>
                    {CAT_LABELS[item.category] ?? item.category}
                  </span>
                </td>
                <td style={{ padding: '5px 6px' }}>
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: item.ranking_status === 'ranked' ? '#d1fae5' : item.ranking_status === 'ranking' ? '#fef9c3' : '#f1f5f9', color: item.ranking_status === 'ranked' ? '#065f46' : item.ranking_status === 'ranking' ? '#92400e' : '#94a3b8', fontWeight: 600 }}>
                    {item.ranking_status}
                  </span>
                </td>
                <td style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>
                  {editId === item.id ? (
                    <span style={{ display: 'flex', gap: 4 }}>
                      <button onClick={handleSaveEdit} disabled={saving} style={{ ...btnPrimary, padding: '1px 8px' }}>{t('common.save', 'Save')}</button>
                      <button onClick={() => setEditId(null)} style={{ ...btnSecondary, padding: '1px 8px' }}>{t('common.cancel', 'Cancel')}</button>
                    </span>
                  ) : (
                    <span style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => { setEditId(item.id); setEditLabel(item.label); }} style={{ ...btnSecondary, padding: '1px 7px' }}>{t('common.edit', 'Edit')}</button>
                      <button onClick={() => handleDelete(item.id)} style={btnDanger}>{t('common.delete', 'Delete')}</button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <div style={{ border: '1px dashed #cbd5e1', borderRadius: 4, padding: '8px 10px', background: '#f8fafc', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
              placeholder={t('claimsWorkspace.step1.labelPlaceholder', 'Item label…')}
              style={{ flex: 1, minWidth: 160, fontSize: 11, padding: '4px 7px', border: '1px solid #94a3b8', borderRadius: 3 }} />
            <select value={newCat} onChange={e => setNewCat(e.target.value as ClaimCategory)} style={{ fontSize: 11, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 3 }}>
              {CAT_OPTIONS.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
            </select>
            <button onClick={handleAdd} disabled={saving || !newLabel.trim()} style={btnPrimary}>{t('common.add', 'Add')}</button>
            <button onClick={() => setAdding(false)} style={btnSecondary}>{t('common.cancel', 'Cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimableItemsStep;
